<template>
  <div>
    <v-data-table
      :headers="computedHeaders"
      :items="proposals"
      :loading="loading"
      item-key="id"
      no-data-text="Không có đề xuất nào"
      :server-items-length="totalItems"
      :footer-props="{
        'items-per-page-text': 'Hiển thị mỗi trang',
        'items-per-page-all-text': 'Tất cả',
        'items-per-page-options': [10, 20, 50]
      }"
      @update:options="onOptionsChange"
    >
      <template v-slot:item.code="{ item }">
        <a class="font-weight-bold primary--text" @click="onViewDetail(item)">{{ item.code }}</a>
      </template>

      <template v-slot:item.priority="{ item }">
        <v-chip x-small :color="getPriorityColor(item.priority)" dark>
          {{ getPriorityLabel(item.priority) }}
        </v-chip>
      </template>

      <template v-slot:item.totalEstimatedCost="{ item }">
        <span class="font-weight-bold">{{ (item.totalEstimatedCost || 0).toLocaleString('vi-VN') }} đ</span>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip small :color="getStatusColor(item.status)" dark>
          {{ getStatusLabel(item.status) }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center justify-center">
          <!-- Nút Phê duyệt và Từ chối cho Quản trị viên khi đề xuất ở trạng thái Chờ duyệt -->
          <template v-if="!isTeacher && item.status === 'SUBMITTED'">
            <v-btn
              icon
              small
              color="success"
              class="mr-1"
              @click="onOpenApprove(item)"
              title="Phê duyệt đề xuất"
            >
              <v-icon small>mdi-check-circle-outline</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="error"
              class="mr-1"
              @click="onOpenReject(item)"
              title="Từ chối đề xuất"
            >
              <v-icon small>mdi-close-circle-outline</v-icon>
            </v-btn>
          </template>

          <v-btn icon small color="primary" class="mr-1" @click="onViewDetail(item)" title="Xem chi tiết đề xuất">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>

          <v-btn
            icon
            small
            color="error"
            @click="onRemove(item)"
            title="Xóa đề xuất"
            v-if="item.status === 'DRAFT' || item.status === 'REJECTED'"
          >
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Dialog chi tiết đề xuất -->
    <ProposalDetailDialog :state="detailDialog" :item="selectedItem" @updated="refresh()" />

    <!-- Dialog phê duyệt đề xuất -->
    <ProposalApproveDialog :state="approveDialog" :item="selectedApproveItem" @updated="refresh()" />

    <!-- Dialog từ chối đề xuất -->
    <ProposalRejectDialog :state="rejectDialog" :item="selectedRejectItem" @updated="refresh()" />
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import ProposalDetailDialog from '@/modules/facility/proposal/ProposalDetailDialog.vue'
import ProposalApproveDialog from '@/modules/facility/proposal/ProposalApproveDialog.vue'
import ProposalRejectDialog from '@/modules/facility/proposal/ProposalRejectDialog.vue'

const headers = [
  { text: 'Mã đề xuất', value: 'code', align: 'left', sortable: false },
  { text: 'Tiêu đề đề xuất', value: 'title', align: 'left', sortable: false },
  { text: 'Người đề xuất', value: 'proposer', align: 'left', sortable: false },
  { text: 'Phòng ban', value: 'department', align: 'left', sortable: false },
  { text: 'Ưu tiên', value: 'priority', align: 'center', sortable: false },
  { text: 'Tổng dự toán', value: 'totalEstimatedCost', align: 'right', sortable: false },
  { text: 'Trạng thái', value: 'status', align: 'center', sortable: false },
  { text: 'Thao tác', value: 'actions', align: 'center', sortable: false, width: '140px' }
]

export default {
  name: 'ProposalDataTable',
  components: {
    ProposalDetailDialog,
    ProposalApproveDialog,
    ProposalRejectDialog
  },
  data() {
    return {
      headers,
      detailDialog: false,
      selectedItem: null,
      approveDialog: false,
      selectedApproveItem: null,
      rejectDialog: false,
      selectedRejectItem: null
    }
  },
  computed: {
    ...mapState('FacilityProposals', ['proposals', 'totalItems', 'pageText', 'loading']),
    ...mapGetters('auth', ['role', 'user', 'profile']),
    isTeacher() {
      return this.role && (this.role.name === 'Teacher' || this.role.type === 'teacher')
    },
    computedHeaders() {
      if (this.isTeacher) {
        return this.headers.filter(h => h.value !== 'proposer')
      }
      return this.headers
    }
  },
  methods: {
    ...mapActions('FacilityProposals', ['requestPageSettings', 'searchProposalList', 'removeProposal']),
    async refresh(query) {
      const q = { ...query }
      if (this.isTeacher) {
        const userId = this.user && (this.user.id || this.user._id)
        if (userId) {
          q.proposerUser = userId
        }
      }
      this.searchProposalList(q)
    },
    onOptionsChange({ page, itemsPerPage }) {
      this.requestPageSettings({ page, itemsPerPage })
    },
    getPriorityLabel(p) {
      switch (p) {
        case 'LOW': return 'Thấp'
        case 'MEDIUM': return 'Bình thường'
        case 'HIGH': return 'Cao'
        default: return p
      }
    },
    getPriorityColor(p) {
      switch (p) {
        case 'LOW': return 'blue-grey'
        case 'MEDIUM': return 'primary'
        case 'HIGH': return 'error'
        default: return 'grey'
      }
    },
    getStatusLabel(status) {
      switch (status) {
        case 'DRAFT': return 'Bản nháp'
        case 'SUBMITTED': return 'Chờ duyệt'
        case 'APPROVED': return 'Đã duyệt'
        case 'REJECTED': return 'Từ chối'
        case 'PURCHASING': return 'Đang mua sắm'
        case 'DONE': return 'Đã hoàn tất'
        default: return status
      }
    },
    getStatusColor(status) {
      switch (status) {
        case 'DRAFT': return 'blue-grey'
        case 'SUBMITTED': return 'warning'
        case 'APPROVED': return 'indigo'
        case 'REJECTED': return 'error'
        case 'PURCHASING': return 'primary'
        case 'DONE': return 'success'
        default: return 'grey'
      }
    },
    onViewDetail(item) {
      this.selectedItem = item
      this.detailDialog = !this.detailDialog
    },
    onOpenApprove(item) {
      this.selectedApproveItem = item
      this.approveDialog = !this.approveDialog
    },
    onOpenReject(item) {
      this.selectedRejectItem = item
      this.rejectDialog = !this.rejectDialog
    },
    onRemove(item) {
      this.$dialog.confirm({
        title: 'Thông báo xác nhận',
        text: `Bạn có chắc muốn xóa đề xuất "${item.code}"?`,
        okText: 'Xóa',
        cancelText: 'Hủy',
        done: async () => {
          this.$loading.active = true
          try {
            await this.removeProposal(item.id)
            this.$alert.deleteSuccess()
          } catch (error) {
            this.$alert.deleteError(error)
          } finally {
            this.$loading.active = false
          }
        }
      })
    }
  }
}
</script>
